import { useState, useEffect, useCallback } from 'react'
import { useDrawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Skeleton } from '@/components/ui/skeleton'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { tenantsService, type Tenant } from '@/services/tenants'
import { toast } from 'sonner'
import {
  Network,
  AlertTriangle,
  Unlink,
  Archive,
  ArrowRightLeft,
} from 'lucide-react'
import {
  allowanceChangeNote,
  allowanceFormState,
  formatSubAccountUsage,
  parseAllowanceInput,
  parseSubAccountUsage,
  subAccountUsageNote,
  type AllowanceMode,
  type SubAccountUsage,
} from '@/lib/subAccountUsage'
import {
  describeDetachOutcome,
  describeSubAccount,
  detachConfirmation,
  selectSubAccounts,
} from '@/lib/subAccountDetach'
import {
  describeNoDestinations,
  describeTransferOutcome,
  eligibleTransferDestinations,
  transferConfirmation,
} from '@/lib/subAccountTransfer'

/**
 * SUB-2.2 · Turning sub-accounts on for one tenant and setting how many it may have.
 *
 * The schema, the rules and the authority shipped in joules-web migration
 * `20260812f`, and nothing called any of it. This drawer is the way in.
 *
 * It validates almost nothing on purpose. The only client-side refusal is an
 * allowance the column's CHECK constraint would reject anyway. Whether the tenant
 * is itself a sub-account (FR6), whether children still exist (FR7) and whether
 * the caller is really a superadmin (FR2) are decided by
 * `trg_enforce_one_level_hierarchy` and `is_superadmin()`, and each of those
 * refusals arrives here as the database's own sentence naming what has to happen
 * first. Restating the rules in TypeScript would be a second definition that
 * drifts, and the one the user reads would be the wrong one.
 *
 * SUB-3.9 added the client-account list below the allowance, and the one action
 * on each: release it. That is FR53/FR55/FR56 and it validates nothing locally
 * either, for a sharper reason than above. FR55 says a detach needs no
 * cooperation from the parent, so `detach_sub_account` reads nothing about the
 * parent at all. Any precondition added here would be a rule the PRD does not
 * have, and it would be the only one the superadmin ever read.
 *
 * SUB-3.10 added the second action on each: move it to a different provider.
 * FR54/FR69/FR56. The picker is the ONE place this file makes a hierarchy
 * decision, and it is a narrowing rather than a rule: it offers only accounts that
 * have sub-accounts enabled and are not archived, so FR69's refusals are rare
 * rather than routine. Every one of those refusals still exists in
 * `transfer_sub_account` and arrives here as the database's own sentence.
 *
 * ⚠️ WHETHER THE DESTINATION HAS ROOM IS ASKED OF THE SERVER, not counted here.
 * `sub_account_usage()` applies FR67 (an archived sub-account does not consume the
 * allowance), so the drawer fetches usage for the chosen destination rather than
 * counting children in the list it already holds. A second count would disagree
 * with the first the moment a sub-account was archived.
 *
 * Not in scope: attaching (SUB-2.4 has the RPC, no surface yet) and the
 * platform-wide parent/child listing (SUB-3.13 FR62). The children here are
 * filtered out of the tenant list this portal already loads.
 */

interface SubAccountsDrawerProps {
  tenantId: string
  tenantName: string
  onSuccess?: () => void
}

// Footer state, shared with the footer component. Same pattern as AddCreditsDrawer.
const formStates = new Map<
  string,
  { handleSave: () => Promise<void>; saving: boolean; loaded: boolean }
>()

export const subAccountsDrawerId = (tenantId: string) => `sub-accounts-${tenantId}`

/** Pulls the readable part out of a FastAPI error, which is where the DB sentence is. */
function describeApiError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (error instanceof Error && error.message) return error.message
  return 'The account could not be updated.'
}

export function SubAccountsDrawer({
  tenantId,
  tenantName,
  onSuccess,
}: SubAccountsDrawerProps) {
  const drawerId = subAccountsDrawerId(tenantId)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [usage, setUsage] = useState<SubAccountUsage | null>(null)

  const [enabled, setEnabled] = useState(false)
  const [mode, setMode] = useState<AllowanceMode>('limited')
  const [input, setInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // SUB-3.9. The children, and the one pending release awaiting confirmation.
  const [children, setChildren] = useState<Tenant[]>([])
  const [childrenError, setChildrenError] = useState<string | null>(null)
  const [pendingDetach, setPendingDetach] = useState<Tenant | null>(null)
  const [detaching, setDetaching] = useState<string | null>(null)
  const [detachError, setDetachError] = useState<string | null>(null)

  // SUB-3.10. The whole tenant list is kept, not only the children, because the
  // destinations come from the same fetch: a second request would be a second
  // snapshot and the two could disagree about who is archived.
  const [allTenants, setAllTenants] = useState<Tenant[]>([])
  const [movingChild, setMovingChild] = useState<Tenant | null>(null)
  const [destinationId, setDestinationId] = useState<string>('')
  const [destinationUsage, setDestinationUsage] = useState<SubAccountUsage | null>(null)
  const [destinationUsageError, setDestinationUsageError] = useState<string | null>(null)
  const [checkingDestination, setCheckingDestination] = useState(false)
  const [pendingTransfer, setPendingTransfer] = useState<Tenant | null>(null)
  const [transferring, setTransferring] = useState<string | null>(null)
  const [transferError, setTransferError] = useState<string | null>(null)

  /**
   * SUB-3.9. The children come from the tenant list this portal already loads,
   * filtered here.
   *
   * ⚠️ A FAILURE TO READ THE LIST IS SHOWN, NOT SWALLOWED. "This parent has no
   * client accounts" and "the list could not be loaded" are different facts, and
   * the second one silently rendered as the first would tell a superadmin that an
   * account they are about to disable sub-accounts on has nothing under it. The
   * same argument `useUsers.ts` makes in joules-web for external grant holders.
   */
  const loadChildren = useCallback(async () => {
    setChildrenError(null)
    try {
      const tenants = await tenantsService.list()
      setAllTenants(tenants)
      setChildren(selectSubAccounts(tenants, tenantId))
    } catch (error) {
      setChildren([])
      setAllTenants([])
      setChildrenError(describeApiError(error))
    }
  }, [tenantId])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const next = parseSubAccountUsage(await tenantsService.getSubAccounts(tenantId))
      setUsage(next)
      setEnabled(next?.enabled ?? false)
      const form = allowanceFormState(next)
      setMode(form.mode)
      setInput(form.input)
      await loadChildren()
    } catch (error) {
      setUsage(null)
      setLoadError(describeApiError(error))
    } finally {
      setLoading(false)
    }
  }, [tenantId, loadChildren])

  /**
   * SUB-3.9 · FR53. One call. The revocation happens inside the RPC's own UPDATE,
   * and the count in the answer is what it removed, so this reports a measurement
   * rather than an intention.
   *
   * Both the usage and the child list are re-read afterwards instead of being
   * patched locally: the allowance line comes from `sub_account_usage()`, which
   * applies FR67, and a local decrement would disagree with it the first time an
   * archived child was involved.
   */
  const handleDetach = useCallback(
    async (child: Tenant) => {
      setDetaching(child.tenant_id)
      setDetachError(null)
      try {
        const outcome = await tenantsService.detachSubAccount(child.tenant_id)
        toast.success(describeDetachOutcome(child.company_name, outcome))
        await load()
        onSuccess?.()
      } catch (error) {
        // Verbatim again: a refusal from the database names what happened, and
        // this is an action a superadmin took on a customer's account.
        setDetachError(describeApiError(error))
      } finally {
        setDetaching(null)
      }
    },
    [load, onSuccess],
  )

  /**
   * SUB-3.10. The destination's usage, asked of the server on selection.
   *
   * This is the only reason the picker knows about the allowance at all, and it is
   * a READ rather than a rule: `sub_account_usage()` applies FR67 and encodes an
   * allowance of 0 as a real cap and NULL as unlimited. The Move button is disabled
   * on `atLimit`, so the operator is stopped by the same number the database will
   * refuse on rather than by a second calculation that could disagree with it.
   *
   * A usage that cannot be read does NOT block the action. The refusal belongs to
   * `transfer_sub_account`, and a failed convenience request must not become a rule
   * this file invented.
   */
  const checkDestination = useCallback(async (id: string) => {
    setDestinationUsage(null)
    setDestinationUsageError(null)
    if (!id) return
    setCheckingDestination(true)
    try {
      setDestinationUsage(parseSubAccountUsage(await tenantsService.getSubAccounts(id)))
    } catch (error) {
      setDestinationUsageError(describeApiError(error))
    } finally {
      setCheckingDestination(false)
    }
  }, [])

  const startMove = useCallback((child: Tenant) => {
    setTransferError(null)
    setDestinationId('')
    setDestinationUsage(null)
    setDestinationUsageError(null)
    setMovingChild((current) =>
      current?.tenant_id === child.tenant_id ? null : child,
    )
  }, [])

  /**
   * SUB-3.10 · FR54. ONE call, and never a detach followed by an attach.
   *
   * Two calls are two transactions, and between them the account has no provider
   * and nobody has agency access to it: exactly the window FR54 exists to forbid.
   * `transfer_sub_account` sets `parent_tenant_id` once and the reconciling trigger
   * does both halves inside that statement, so the counts in the answer are what it
   * removed and what it created.
   *
   * Both the usage line and the child list are re-read afterwards rather than
   * patched locally, for the same reason as the release path: the allowance comes
   * from `sub_account_usage()`, which applies FR67.
   */
  const handleTransfer = useCallback(
    async (child: Tenant, destination: Tenant) => {
      setTransferring(child.tenant_id)
      setTransferError(null)
      try {
        const outcome = await tenantsService.transferSubAccount(
          child.tenant_id,
          destination.tenant_id,
        )
        toast.success(
          describeTransferOutcome(
            child.company_name,
            destination.company_name,
            outcome,
          ),
        )
        setMovingChild(null)
        setDestinationId('')
        setDestinationUsage(null)
        await load()
        onSuccess?.()
      } catch (error) {
        // Verbatim: a refusal from the database names what happened, and this is
        // the single most sensitive action in the hierarchy. It changes which
        // agency can read a customer's data.
        setTransferError(describeApiError(error))
      } finally {
        setTransferring(null)
      }
    },
    [load, onSuccess],
  )

  useEffect(() => {
    void load()
  }, [load])

  // SUB-3.10. The chosen destination, resolved from the list the drawer holds. Kept
  // as a derivation rather than a second piece of state so that the name in the
  // confirmation and the id sent to the server cannot disagree.
  const transferTarget =
    allTenants.find((t) => t.tenant_id === destinationId) ?? null

  const parsed = parseAllowanceInput(mode, input)
  const pendingNote = enabled && usage ? allowanceChangeNote(parsed.value, usage.used) : null

  const handleSave = useCallback(async () => {
    // The one client-side refusal. See the note at the top.
    if (enabled && parsed.error) {
      setSaveError(parsed.error)
      return
    }

    setSaving(true)
    setSaveError(null)
    try {
      const result = parseSubAccountUsage(
        await tenantsService.setSubAccounts(tenantId, {
          enabled,
          allowance: enabled ? parsed.value : null,
        }),
      )
      setUsage(result)
      const form = allowanceFormState(result)
      setMode(form.mode)
      setInput(form.input)
      setEnabled(result?.enabled ?? false)
      toast.success(
        result?.enabled
          ? `Sub-accounts enabled for ${tenantName}`
          : `Sub-accounts disabled for ${tenantName}`,
      )
      onSuccess?.()
    } catch (error) {
      // Kept verbatim: FR7's refusal names what has to happen first, and a fixed
      // string would throw away the only part the super admin can act on.
      setSaveError(describeApiError(error))
    } finally {
      setSaving(false)
    }
  }, [enabled, parsed.error, parsed.value, tenantId, tenantName, onSuccess])

  useEffect(() => {
    formStates.set(drawerId, { handleSave, saving, loaded: !loading && !loadError })
    return () => {
      formStates.delete(drawerId)
    }
  }, [drawerId, handleSave, saving, loading, loadError])

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (loadError || !usage) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
        <p className="text-sm text-destructive">
          {loadError ?? 'This account is not available.'}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => void load()}>
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Network className="h-5 w-5 text-primary mt-0.5" />
        <div>
          <h3 className="font-semibold">{tenantName}</h3>
          <p className="text-sm text-muted-foreground">
            Sub-accounts are off for every account until they are enabled here.
          </p>
        </div>
      </div>

      {/* FR5. The count comes from the database, which applies FR67. */}
      <div className="rounded-md border bg-muted/40 px-4 py-3">
        <p className="text-sm font-medium">
          {usage.enabled
            ? formatSubAccountUsage(usage)
            : 'Sub-accounts are off for this account.'}
        </p>
        {usage.enabled && subAccountUsageNote(usage) && (
          <p className="mt-1 text-xs text-muted-foreground">{subAccountUsageNote(usage)}</p>
        )}
      </div>

      {/* SUB-3.9 · FR53, FR55, FR56. The client accounts, and the one action on
          each. Shown whenever there is something to show, including when the
          capability has since been switched off in this form but not yet saved:
          rule 4 refuses that save while children exist, and hiding the children
          at that moment would hide the reason. */}
      {(children.length > 0 || childrenError) && (
        <div className="space-y-2">
          <Label>Client accounts</Label>

          {childrenError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-destructive">
                  The client accounts beneath this account could not be loaded, so
                  this list may be incomplete. {childrenError}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => void loadChildren()}
                >
                  Try again
                </Button>
              </div>
            </div>
          )}

          {children.map((child) => {
            const isMoving = movingChild?.tenant_id === child.tenant_id
            // SUB-3.10. Recomputed per child rather than once, because the current
            // provider is one of the exclusions and it is a property of the child.
            const destinations = eligibleTransferDestinations(allTenants, child)
            const emptyReason = describeNoDestinations(
              destinations.length,
              childrenError !== null,
            )
            const destination =
              destinations.find((d) => d.tenant_id === destinationId) ?? null
            const busy = detaching !== null || transferring !== null || saving

            return (
              <div key={child.tenant_id} className="rounded-md border">
                <div className="flex items-start justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      {child.is_archived && (
                        <Archive
                          className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0"
                          aria-hidden
                        />
                      )}
                      <span className="truncate">{child.company_name}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {describeSubAccount(child)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* SUB-3.10 · FR54. Opens the picker; it moves nothing. */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startMove(child)}
                      disabled={busy}
                    >
                      <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                      {transferring === child.tenant_id ? 'Moving...' : 'Move'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingDetach(child)}
                      disabled={busy}
                    >
                      <Unlink className="h-3.5 w-3.5 mr-1.5" />
                      {detaching === child.tenant_id ? 'Releasing...' : 'Release'}
                    </Button>
                  </div>
                </div>

                {isMoving && (
                  <div className="border-t px-3 py-3 space-y-3">
                    <Label htmlFor={'destination-' + child.tenant_id}>Move to</Label>

                    {emptyReason ? (
                      <p className="text-xs text-muted-foreground">{emptyReason}</p>
                    ) : (
                      <>
                        <Select
                          value={destinationId}
                          onValueChange={(v: string) => {
                            setDestinationId(v)
                            void checkDestination(v)
                          }}
                          disabled={transferring !== null}
                        >
                          <SelectTrigger id={'destination-' + child.tenant_id}>
                            <SelectValue placeholder="Choose a provider" />
                          </SelectTrigger>
                          <SelectContent>
                            {destinations.map((d) => (
                              <SelectItem key={d.tenant_id} value={d.tenant_id}>
                                {d.company_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* FR5 / FR67. The server's own count for the chosen
                            destination. Never computed here. */}
                        {checkingDestination && (
                          <p className="text-xs text-muted-foreground">
                            Checking that provider&apos;s allowance...
                          </p>
                        )}
                        {destinationUsage && !checkingDestination && (
                          <p className="text-xs text-muted-foreground">
                            {formatSubAccountUsage(destinationUsage)}
                            {subAccountUsageNote(destinationUsage)
                              ? ' ' + subAccountUsageNote(destinationUsage)
                              : ''}
                          </p>
                        )}
                        {/* A failed convenience read is stated and does NOT block:
                            the refusal belongs to transfer_sub_account. */}
                        {destinationUsageError && !checkingDestination && (
                          <p className="text-xs text-muted-foreground">
                            That provider&apos;s allowance could not be read, so it
                            will be checked when you confirm. {destinationUsageError}
                          </p>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => setPendingTransfer(child)}
                            disabled={
                              !destination ||
                              transferring !== null ||
                              checkingDestination ||
                              destinationUsage?.atLimit === true
                            }
                          >
                            Move account
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMovingChild(null)}
                            disabled={transferring !== null}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {children.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Releasing or moving an account moves no data. It keeps everything it
              has, and its own account id, either way.
            </p>
          )}
        </div>
      )}

      {detachError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">{detachError}</p>
        </div>
      )}

      {transferError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">{transferError}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="sub-accounts-enabled">Sub-accounts enabled</Label>
          <p className="text-xs text-muted-foreground">
            Lets this account hold client accounts beneath it.
          </p>
        </div>
        <Switch
          id="sub-accounts-enabled"
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={saving}
        />
      </div>

      {/* FR3. Two choices, made explicitly. An empty box is not unlimited. */}
      {enabled && (
        <div className="space-y-3">
          <Label>Allowance</Label>
          <RadioGroup
            value={mode}
            onValueChange={(v: string) => setMode(v as AllowanceMode)}
            disabled={saving}
            className="space-y-2"
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="limited" id="allowance-limited" />
              <Label htmlFor="allowance-limited" className="font-normal">
                Limit to
              </Label>
              <Input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                className="w-24"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value)
                  setMode('limited')
                }}
                disabled={saving}
                aria-label="Maximum sub-accounts"
              />
              <span className="text-sm text-muted-foreground">sub-accounts</span>
            </div>
            <div className="flex items-center gap-3">
              <RadioGroupItem value="unlimited" id="allowance-unlimited" />
              <Label htmlFor="allowance-unlimited" className="font-normal">
                Unlimited
              </Label>
            </div>
          </RadioGroup>

          {/* FR68, at the point of editing. Information, never a blocker. */}
          {pendingNote && <p className="text-xs text-muted-foreground">{pendingNote}</p>}
        </div>
      )}

      {saveError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">{saveError}</p>
        </div>
      )}

      {/* SUB-3.9. The confirmation names what will be revoked and states that the
          data stays, because FR56 is the guarantee the action is FOR and "are you
          sure?" would leave the superadmin to guess. It does not predict the
          number of grants: that comes back from the detach itself, measured. */}
      <ConfirmDialog
        open={pendingDetach !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDetach(null)
        }}
        title={
          pendingDetach
            ? detachConfirmation(pendingDetach.company_name, tenantName).title
            : ''
        }
        description={
          pendingDetach
            ? detachConfirmation(pendingDetach.company_name, tenantName).description
            : ''
        }
        confirmLabel={
          pendingDetach
            ? detachConfirmation(pendingDetach.company_name, tenantName).confirmLabel
            : 'Confirm'
        }
        destructive
        onConfirm={() => {
          const child = pendingDetach
          setPendingDetach(null)
          if (child) void handleDetach(child)
        }}
      />

      {/* SUB-3.10. The confirmation names BOTH providers, states that the access
          change happens in one step with no gap (FR54) and that no data moves
          (FR56). This is the single most sensitive action in the hierarchy: it
          changes which agency can read a customer's data, and "are you sure?" would
          leave the operator to guess at every part of it. It predicts no counts;
          those come back from the transfer itself, measured.

          The destination is resolved from the tenant list rather than held in state
          alongside the id, so a list that refreshed under the dialog cannot leave a
          stale name in the sentence the operator is agreeing to. */}
      <ConfirmDialog
        open={pendingTransfer !== null && transferTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTransfer(null)
        }}
        title={
          pendingTransfer && transferTarget
            ? transferConfirmation(
                pendingTransfer.company_name,
                tenantName,
                transferTarget.company_name,
              ).title
            : ''
        }
        description={
          pendingTransfer && transferTarget
            ? transferConfirmation(
                pendingTransfer.company_name,
                tenantName,
                transferTarget.company_name,
              ).description
            : ''
        }
        confirmLabel={
          pendingTransfer && transferTarget
            ? transferConfirmation(
                pendingTransfer.company_name,
                tenantName,
                transferTarget.company_name,
              ).confirmLabel
            : 'Confirm'
        }
        destructive
        onConfirm={() => {
          const child = pendingTransfer
          const destination = transferTarget
          setPendingTransfer(null)
          if (child && destination) void handleTransfer(child, destination)
        }}
      />
    </div>
  )
}

export function SubAccountsFooter({ drawerId }: { drawerId: string }) {
  const { closeDrawer } = useDrawer()
  const [formState, setFormState] = useState(formStates.get(drawerId))

  useEffect(() => {
    const interval = setInterval(() => {
      setFormState(formStates.get(drawerId))
    }, 100)
    return () => clearInterval(interval)
  }, [drawerId])

  return (
    <DrawerFooter>
      <Button
        variant="outline"
        onClick={() => closeDrawer(drawerId)}
        disabled={formState?.saving}
      >
        Close
      </Button>
      <Button
        onClick={() => formState?.handleSave()}
        disabled={formState?.saving || !formState?.loaded}
      >
        {formState?.saving ? 'Saving...' : 'Save'}
      </Button>
    </DrawerFooter>
  )
}
